import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { SX, toLeaderboardRow } from './leaderboardData';
import TopPerformerCard from './TopPerformerCard';

// Cards rendered up front, and how many more each time the tail comes into
// view. One screen fits three or four, so a dozen covers the first scroll with
// room to spare.
const CHUNK = 12;

const scrollerSx = {
  display: 'flex',
  gap: 2,
  overflowX: 'auto',
  pt: 0.5,
  pb: 1.5,
  '&::-webkit-scrollbar': { height: 8 },
  '&::-webkit-scrollbar-thumb': { background: SX.border, borderRadius: 999 },
};

/**
 * The horizontally scrolling "Top Performers by Batch" rail.
 *
 * Two things keep this cheap as the academy grows. It renders the toppers the
 * API returned rather than a card per batch in the batch list — only batches
 * that have earned XP have a top performer, and the rest were rendering
 * hundreds of identical "no XP yet" cards. And it reveals the cards a chunk at
 * a time as the tail is scrolled towards, so the DOM holds what has been looked
 * at rather than every batch at once. The API orders by XP descending, so the
 * head of the rail is the part worth seeing first.
 */
export default function TopPerformersRail({ toppers = [], loading, silentBatchCount = 0 }) {
  const [visible, setVisible] = useState(CHUNK);
  const scrollerRef = useRef(null);
  const sentinelRef = useRef(null);

  // A new payload starts the window over.
  useEffect(() => {
    setVisible(CHUNK);
  }, [toppers]);

  useEffect(() => {
    if (visible >= toppers.length) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible((v) => Math.min(v + CHUNK, toppers.length));
        }
      },
      // Widen the root so the next chunk is mounted before the gap is reached.
      { root: scrollerRef.current, rootMargin: '600px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visible, toppers.length]);

  // Mapping to row shape is per-card work; do it once for the slice on screen.
  const shown = useMemo(
    () =>
      toppers.slice(0, visible).map((t) => ({
        batchId: t.batchId,
        batchName: t.batchName,
        student: toLeaderboardRow(t.student),
      })),
    [toppers, visible],
  );

  if (loading && !toppers.length) {
    return (
      <Box sx={scrollerSx}>
        {[0, 1, 2, 3].map((i) => (
          <TopPerformerCard key={i} loading />
        ))}
      </Box>
    );
  }

  if (!toppers.length) {
    return (
      <Stack
        alignItems="center"
        spacing={1}
        sx={{
          py: 4,
          mb: 1.5,
          borderRadius: `${SX.radius.lg}px`,
          border: `1px dashed ${SX.border}`,
          background: SX.cardSoft,
        }}
      >
        <Icon icon="solar:ranking-linear" width={30} height={30} color={SX.textMuted} />
        <Typography sx={{ fontSize: 13.5, color: SX.textMuted }}>
          No batch has earned XP yet — cards appear as learners start earning.
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <Box ref={scrollerRef} sx={scrollerSx}>
        {shown.map(({ batchId, batchName, student }, i) => (
          <TopPerformerCard key={batchId} index={i} batchName={batchName} student={student} />
        ))}

        {/* Tail marker — crossing it mounts the next chunk. */}
        {visible < toppers.length && <Box ref={sentinelRef} sx={{ flex: '0 0 1px', minHeight: 1 }} />}
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3, px: 0.25 }}>
        <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>
          Showing {Math.min(visible, toppers.length)} of {toppers.length} batches with XP
          {visible < toppers.length ? ' — scroll for more' : ''}
        </Typography>
        {silentBatchCount > 0 && (
          <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>
            · {silentBatchCount} other {silentBatchCount === 1 ? 'batch has' : 'batches have'} not earned yet
          </Typography>
        )}
      </Stack>
    </>
  );
}
